/**
 * MultiDiseaseExperimentModal - Modal for multi-disease experiment users
 *
 * Shows a flow explaining that only cancer risk is available today,
 * collects feedback on disease priorities, and offers winback if they want to cancel.
 *
 * Flow states:
 * - landing: Initial message about health profile rolling out
 * - not-expecting: "We get it" screen with continue/cancel options
 * - feedback-continue: Collect priorities for users continuing
 * - feedback-cancel: Collect priorities for users wanting to cancel
 * - winback: Discount offer for cancelling users
 * - success: Winback accepted confirmation
 * - cancelled: Trial cancelled confirmation
 */

import { useState, useCallback, useEffect } from 'react';
import { usePaywallCancellation } from '@/components/payment-paywall-modal/usePaywallCancellation';
import { CancellationReason } from '@/api/generated-catch-backend/model';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { MULTI_VERTICAL_EXPERIMENT_NAME } from '@/constants/experiments';
import { useMultiDiseaseFeedback, type HealthPriorities } from './useMultiDiseaseFeedback';
import { LandingStep } from './components/LandingStep';
import { NotExpectingStep } from './components/NotExpectingStep';
import { FeedbackStep } from './components/FeedbackStep';
import { WinbackStep } from './components/WinbackStep';
import { SuccessStep } from './components/SuccessStep';
import { CancelledStep } from './components/CancelledStep';
import { ModalErrorBoundary } from './ModalErrorBoundary';
import './multi-disease-reset.css';

export type ModalStep =
  | 'landing'
  | 'not-expecting'
  | 'feedback-continue'
  | 'feedback-cancel'
  | 'winback'
  | 'success'
  | 'cancelled';

interface MultiDiseaseExperimentModalProps {
  /** Stripe subscription ID (needed for winback flow) */
  subscriptionId: string | null;
  /** Callback when modal is dismissed (user continues with Catch) */
  onDismiss: () => void;
  /** Callback when trial is cancelled */
  onCancelled: () => void;
}

export function MultiDiseaseExperimentModal({
  subscriptionId,
  onDismiss,
  onCancelled,
}: MultiDiseaseExperimentModalProps) {
  const [step, setStep] = useState<ModalStep>('landing');

  // Analytics
  const {
    callMultiDiseaseModalViewed,
    callMultiDiseaseFeedbackSubmitted,
    callWinbackAccepted,
    callWinbackDeclined,
  } = useAnalyticsCalls();

  // Feedback submission
  const feedback = useMultiDiseaseFeedback();

  // Winback flow (reuse from paywall)
  const cancellation = usePaywallCancellation(subscriptionId);

  // Track collected priorities for feedback submission
  const [collectedPriorities, setCollectedPriorities] = useState<HealthPriorities | null>(null);

  // Track step views for analytics
  useEffect(() => {
    callMultiDiseaseModalViewed(step, 'multi-disease');
  }, [step, callMultiDiseaseModalViewed]);

  // --- Step navigation handlers ---

  const handleContinueFromLanding = useCallback(() => {
    setStep('feedback-continue');
  }, []);

  const handleNotExpecting = useCallback(() => {
    setStep('not-expecting');
  }, []);

  const handleContinueWithCatch = useCallback(() => {
    setStep('feedback-continue');
  }, []);

  const handleCancelTrial = useCallback(() => {
    setStep('feedback-cancel');
  }, []);

  const handleFeedbackContinueSubmit = useCallback(
    async (priorities: HealthPriorities, additionalFeedback?: string) => {
      try {
        await feedback.submitFeedback(priorities, 'continue', additionalFeedback);
        // Track feedback submitted
        callMultiDiseaseFeedbackSubmitted(priorities, 'continue', !!additionalFeedback);
        onDismiss();
      } catch {
        // Error handled in hook
      }
    },
    [feedback, onDismiss, callMultiDiseaseFeedbackSubmitted]
  );

  const handleFeedbackCancelSubmit = useCallback(
    async (priorities: HealthPriorities, additionalFeedback?: string) => {
      setCollectedPriorities(priorities);

      try {
        // Submit feedback first
        await feedback.submitFeedback(priorities, 'cancel', additionalFeedback);
        // Track feedback submitted
        callMultiDiseaseFeedbackSubmitted(priorities, 'cancel', !!additionalFeedback);

        // Then create cancellation request to get winback pricing
        if (subscriptionId) {
          // Use OTHER as reason since this is from experiment flow
          await cancellation.submitCancelReason(subscriptionId, CancellationReason.OTHER, 'Multi-disease experiment cancellation');
          setStep('winback');
        }
      } catch {
        // Error handled in hooks
      }
    },
    [feedback, subscriptionId, cancellation, callMultiDiseaseFeedbackSubmitted]
  );

  const handleWinbackAccept = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      await cancellation.acceptWinback(subscriptionId);
      // Track winback accepted with pricing info (convert cents to dollars)
      if (cancellation.winbackPricing) {
        callWinbackAccepted(
          cancellation.winbackPricing.finalPriceCents / 100,
          cancellation.winbackPricing.discountCents / 100,
          MULTI_VERTICAL_EXPERIMENT_NAME
        );
      }
      setStep('success');
    } catch {
      // Error handled in hook
    }
  }, [subscriptionId, cancellation, callWinbackAccepted]);

  const handleWinbackDecline = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      await cancellation.declineWinback(subscriptionId);
      // Track winback declined with source
      callWinbackDeclined(MULTI_VERTICAL_EXPERIMENT_NAME);
      setStep('cancelled');
    } catch {
      // Error handled in hook
    }
  }, [subscriptionId, cancellation, callWinbackDeclined]);

  const handleSuccessDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  const handleCancelledDismiss = useCallback(() => {
    onCancelled();
  }, [onCancelled]);

  // Dismiss via X button (skip feedback)
  const handleDismissX = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  // --- Render step content ---

  const renderContent = () => {
    switch (step) {
      case 'landing':
        return (
          <LandingStep
            onContinue={handleContinueFromLanding}
            onNotExpecting={handleNotExpecting}
          />
        );

      case 'not-expecting':
        return (
          <NotExpectingStep
            onContinue={handleContinueWithCatch}
            onCancel={handleCancelTrial}
          />
        );

      case 'feedback-continue':
        return (
          <FeedbackStep
            flowPath="continue"
            onSubmit={handleFeedbackContinueSubmit}
            isSubmitting={feedback.isSubmitting}
            error={feedback.error}
          />
        );

      case 'feedback-cancel':
        return (
          <FeedbackStep
            flowPath="cancel"
            onSubmit={handleFeedbackCancelSubmit}
            isSubmitting={feedback.isSubmitting || cancellation.isSubmitting}
            error={feedback.error || cancellation.error}
          />
        );

      case 'winback':
        return (
          <WinbackStep
            pricing={cancellation.winbackPricing}
            isPricingLoading={cancellation.isPricingLoading}
            onAccept={handleWinbackAccept}
            onDecline={handleWinbackDecline}
            isSubmitting={cancellation.isSubmitting}
            error={cancellation.error}
          />
        );

      case 'success':
        return <SuccessStep onDismiss={handleSuccessDismiss} />;

      case 'cancelled':
        return <CancelledStep onDismiss={handleCancelledDismiss} />;

      default:
        return null;
    }
  };

  // Show X button on landing and not-expecting steps
  const showCloseButton = step === 'landing' || step === 'not-expecting';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4">
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        {/* Close button */}
        {showCloseButton && (
          <button
            onClick={handleDismissX}
            className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Scrollable content area with error boundary */}
        <div className="multi-disease-reset max-h-[90vh] overflow-y-auto px-7 py-10">
          <ModalErrorBoundary onDismiss={onDismiss}>
            {renderContent()}
          </ModalErrorBoundary>
        </div>
      </div>
    </div>
  );
}

export default MultiDiseaseExperimentModal;
